import {
  type AccountingPurchaseOrderLineItemCost,
  type Currency,
  type InvoiceLineItemCost,
  useApiDealsV1CostAposList,
  useApiDealsV1CostsList,
} from 'api';
import { type I18nTranslateFunction, useTranslation } from 'i18n';
import { useParams } from 'react-router-dom';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  CircularProgress,
  Container,
  DataGrid,
  formatCurrency,
  type GridColDef,
  Link,
  Page,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from 'ui';
import { ArrowCircleLeft, ExpandMore } from 'ui/icons';

import type { InvoiceStatus } from '../../../invoice/enums';
import { getColorForInvoiceStatus } from '../getColorForInvoiceStatus';
import type { ApoLineItem, InvoiceLineItem } from './types';

const getColumns = (t: I18nTranslateFunction<string>): GridColDef<InvoiceLineItem>[] => [
  { field: 'quantity', headerName: t('dealHub.dealCostDetails.columns.quantity') },
  {
    field: 'unit_amount',
    headerName: t('dealHub.dealCostDetails.columns.unitAmount'),
  },
  {
    field: 'description',
    headerName: t('dealHub.dealCostDetails.columns.description'),
    renderCell: (params) => <Tooltip title={params.value}>{params.value}</Tooltip>,
    width: 300,
  },
  {
    field: 'line_total',
    headerName: t('dealHub.dealCostDetails.columns.lineTotal'),
    valueGetter: (_value, row) => {
      if (row.quantity && row.unit_amount) {
        return formatCurrency(row.quantity * row.unit_amount, {
          currencyCode: row.invoice_currency_code,
        });
      }
    },
  },
  {
    field: 'invoice_status',
    headerName: t('dealHub.dealCostDetails.columns.invoiceStatus'),
    renderCell: (params) => {
      const statusColor = getColorForInvoiceStatus(params.value);
      return <Chip label={params.value} color={statusColor} />;
    },
    width: 150,
  },
  { field: 'invoice_xero_id', headerName: t('dealHub.dealCostDetails.columns.invoiceXeroId') },
  {
    field: 'invoice_xero_amount_paid',
    headerName: t('dealHub.dealCostDetails.columns.invoiceXeroAmountPaid'),
  },
  {
    field: 'invoice_xero_fully_paid_on',
    headerName: t('dealHub.dealCostDetails.columns.invoiceXeroFullyPaidOn'),
  },
];

export const DealCostDetails = () => {
  const { t } = useTranslation('orders');
  const { deal_id } = useParams();
  const columns = getColumns(t);

  const parsedDealId = deal_id ? Number(deal_id) : NaN;
  const isValidDealId = Number.isFinite(parsedDealId);
  const dealId = isValidDealId ? parsedDealId : 0;

  const { data: invoiceLineResponse, isLoading: isInvoiceLineLoading } = useApiDealsV1CostsList<InvoiceLineItem[]>(
    dealId,
    {
      query: {
        enabled: isValidDealId,
        select: (items: InvoiceLineItemCost[]) => items.map(mapInvoiceLineItem),
      },
    }
  );

  const { data: apoLineResponse, isLoading: isApoLineLoading } = useApiDealsV1CostAposList<ApoLineItem[]>(dealId, {
    query: {
      enabled: isValidDealId,
      select: (items: AccountingPurchaseOrderLineItemCost[]) => items.map(mapApoLineItem),
    },
  });

  if (isInvoiceLineLoading || isApoLineLoading)
    return (
      <Box sx={{ display: 'flex', mt: 10 }}>
        <CircularProgress />
      </Box>
    );

  const apoLineData = apoLineResponse ?? [];
  const invoiceLineData = invoiceLineResponse ?? [];

  return (
    <Page>
      <Box sx={{ mt: 5 }}>
        <Link href={`/post-deal/dealhub/deal/details/${deal_id}/`}>
          <ArrowCircleLeft />
          {t('dealHub.dealCostDetails.actions.backToDeal', { values: { dealId: deal_id } })}
        </Link>
      </Box>

      <Box sx={{ height: '80vh', mt: 1, overflow: 'scroll', width: '90vw' }}>
        {apoLineData.map((apoLine: ApoLineItem) => (
          <Accordion defaultExpanded={false} sx={{ mb: 2 }} key={apoLine.id}>
            <AccordionSummary expandIcon={<ExpandMore />} aria-controls="panel1-content" id="panel1-header">
              <Container>
                <Typography noWrap variant="h6">
                  {apoLine.description}
                </Typography>
              </Container>
            </AccordionSummary>
            <AccordionDetails>
              <Table sx={{ mb: 5 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('dealHub.dealCostDetails.columns.quantity')}</TableCell>
                    <TableCell>{t('dealHub.dealCostDetails.columns.unitAmount')}</TableCell>
                    <TableCell>{t('dealHub.dealCostDetails.columns.total')}</TableCell>
                    <TableCell>{t('dealHub.dealCostDetails.columns.status')}</TableCell>
                    <TableCell>{t('dealHub.dealCostDetails.columns.currencyCode')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>{apoLine.quantity}</TableCell>
                    <TableCell>{apoLine.unit_amount}</TableCell>
                    <TableCell>{apoLine.quantity * apoLine.unit_amount}</TableCell>
                    <TableCell>{apoLine.status}</TableCell>
                    <TableCell>{apoLine.currency_code}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
              <Typography variant="h6">{t('dealHub.dealCostDetails.sections.invoiceLineItems')}</Typography>
              <DataGrid
                rows={invoiceLineData.filter(
                  (invoiceLine: InvoiceLineItem) => invoiceLine.accounting_purchase_order_line_item_id === apoLine.id
                )}
                columns={columns}
                hideFooter
                sx={{ overflowX: 'scroll' }}
              />
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>
    </Page>
  );
};

const mapInvoiceLineItem = (item: InvoiceLineItemCost): InvoiceLineItem => {
  const toNumberOrNull = (value?: string | null) => {
    if (!value) {
      return null;
    }
    const parsed = Number(value);
    return Number.isNaN(parsed) ? null : parsed;
  };

  const extras = item as {
    invoice_xero_amount_paid?: string | number | null;
    invoice_xero_fully_paid_on?: string | null;
  };
  const invoiceXeroAmountPaid = extras.invoice_xero_amount_paid;

  return {
    account_code: null,
    accounting_purchase_order_line_item_id: item.accounting_purchase_order_line_item_id ?? null,
    apo_description: item.apo_description,
    apo_quantity: toNumberOrNull(item.apo_quantity),
    apo_unit_amount: toNumberOrNull(item.apo_unit_amount),
    category: item.apo_status,
    description: item.description,
    expense_category: null,
    id: item.id,
    invoice_currency_code: item.invoice_currency_code as Currency,
    invoice_id: item.invoice_id,
    invoice_status: item.invoice_status as InvoiceStatus,
    invoice_type: '',
    invoice_xero_amount_paid:
      typeof invoiceXeroAmountPaid === 'string' ? Number(invoiceXeroAmountPaid) : (invoiceXeroAmountPaid ?? 0),
    invoice_xero_fully_paid_on: extras.invoice_xero_fully_paid_on ?? null,
    invoice_xero_id: item.invoice_xero_id ?? null,
    managed_from: '',
    quantity: toNumberOrNull(item.quantity),
    unit_amount: toNumberOrNull(item.unit_amount),
    xero_id: item.xero_id ?? null,
  };
};

const mapApoLineItem = (item: AccountingPurchaseOrderLineItemCost): ApoLineItem => {
  const toNumber = (value?: string | null) => {
    if (!value) {
      return 0;
    }
    const parsed = Number(value);
    return Number.isNaN(parsed) ? 0 : parsed;
  };

  return {
    cost_type: item.cost_type,
    currency_code: item.currency_code as Currency,
    description: item.description,
    id: item.id,
    is_cost_planned: item.is_cost_planned,
    quantity: toNumber(item.quantity),
    status: item.status as InvoiceStatus,
    unit_amount: toNumber(item.unit_amount),
  };
};
